export type DateTimeValue = Date | number | string;

export interface LocalePreferences {
  locales?: Intl.LocalesArgument;
  timeZone?: string;
  fallback?: string;
}

function deviceLocales(): Intl.LocalesArgument | undefined {
  if (typeof navigator === "undefined") return undefined;
  return navigator.languages.length > 0 ? navigator.languages : navigator.language;
}

export function userLocalePreferences(): Required<Pick<LocalePreferences, "locales" | "timeZone">> {
  return {
    locales: deviceLocales() ?? "en",
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
  };
}

function validDate(value: DateTimeValue): Date | null {
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function preferences(options: LocalePreferences) {
  const device = userLocalePreferences();
  return {
    locales: options.locales ?? device.locales,
    timeZone: options.timeZone ?? device.timeZone,
    fallback: options.fallback ?? "—",
  };
}

export function formatLocalDate(value: DateTimeValue, options: LocalePreferences = {}): string {
  const date = validDate(value);
  const resolved = preferences(options);
  if (!date) return resolved.fallback;
  return new Intl.DateTimeFormat(resolved.locales, {
    dateStyle: "medium",
    timeZone: resolved.timeZone,
  }).format(date);
}

export function formatLocalDateTime(value: DateTimeValue, options: LocalePreferences = {}): string {
  const date = validDate(value);
  const resolved = preferences(options);
  if (!date) return resolved.fallback;
  return new Intl.DateTimeFormat(resolved.locales, {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: resolved.timeZone,
  }).format(date);
}

export function formatLocalTime(value: DateTimeValue, options: LocalePreferences = {}): string {
  const date = validDate(value);
  const resolved = preferences(options);
  if (!date) return resolved.fallback;
  return new Intl.DateTimeFormat(resolved.locales, {
    hour: "numeric",
    minute: "2-digit",
    timeZone: resolved.timeZone,
  }).format(date);
}

export function localDateInputValue(
  value: DateTimeValue = new Date(),
  options: Pick<LocalePreferences, "timeZone"> = {},
): string {
  const date = validDate(value);
  if (!date) return "";
  const timeZone = options.timeZone ?? userLocalePreferences().timeZone;
  const parts = new Intl.DateTimeFormat("en-CA-u-ca-iso8601-nu-latn", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone,
  }).formatToParts(date);
  const year = parts.find((part) => part.type === "year")?.value;
  const month = parts.find((part) => part.type === "month")?.value;
  const day = parts.find((part) => part.type === "day")?.value;
  return year && month && day ? `${year}-${month}-${day}` : "";
}
